<script lang="ts">
  import {
    AlertCircle,
    AlertTriangle,
    Bell,
    BellOff,
    Check,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Info,
    Loader2,
    RotateCw,
    X,
    XCircle,
    Zap,
  } from 'lucide-svelte';
  import type {
    ParityCheckDetails,
    ParityCheckResult,
    ParityInterval,
    ParityStatus,
  } from '@rhdashboard-vnext/shared';
  import { DEFAULT_PARITY_TOLERANCES } from '@rhdashboard-vnext/shared';
  import {
    PARITY_INTERVAL_OPTIONS,
    fetchParityAlert,
    fetchParityDetails,
    runParityCheck,
    setParityAlert,
  } from '$lib/api/parity';
  import { Badge } from '$lib/components/ui/badge';
  import { Button } from '$lib/components/ui/button';
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
  } from '$lib/components/ui/dialog';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from '$lib/components/ui/dropdown-menu';
  import CopyableSessionId from './CopyableSessionId.svelte';

  // ============================================================
  // <ParityCheckModal>  -  Fase 6.
  //
  // Modal del flujo "⚖️ Run Parity Check". Dispara POST /api/parity/check
  // contra el upstream (BigTester/LilTester/PepeFlow), renderea summary
  // table + slippage + 2 secciones collapsable lazy-load.
  //
  // El upstream tarda 1-60s (depende del backlog). Mientras corre:
  //   - spinner + counter de segundos transcurridos
  //   - boton Cancel que aborta el fetch via AbortController
  //   - despues de 90s, mensaje "esto esta tardando mas de lo normal"
  //
  // Click en el status badge -> abre WarmupParityModal (callback al parent).
  // ============================================================

  interface Props {
    open: boolean;
    sessionId: string | null;
    onClose: () => void;
    /** Callback opcional cuando el usuario quiere ver detalles de warmup parity. */
    onOpenWarmup?: (sessionId: string) => void;
  }

  let { open = $bindable(), sessionId, onClose, onOpenWarmup }: Props = $props();

  let loading = $state(false);
  let elapsed = $state(0);
  let error = $state<string | null>(null);
  let result = $state<ParityCheckResult | null>(null);

  // Details (lazy load)
  // Accordion mutex: solo una seccion abierta a la vez. Evita scroll
  // exagerado cuando ambas tienen 50+ rows.
  type DiffSection = 'block' | 'trade' | null;
  let expandedSection = $state<DiffSection>(null);
  let blockExpanded = $derived(expandedSection === 'block');
  let tradeExpanded = $derived(expandedSection === 'trade');

  // Paginacion cliente-side de las dos listas. El upstream devuelve TODOS los
  // results sin truncar — paginamos en cliente para no scrollear 50+ rows.
  const DIFF_PAGE_SIZE = 25;
  let blockPage = $state(1);
  let tradePage = $state(1);

  let details = $state<ParityCheckDetails | null>(null);
  let detailsLoading = $state(false);
  let detailsError = $state<string | null>(null);

  let blockTotalPages = $derived(
    details ? Math.max(1, Math.ceil(details.blocks.results.length / DIFF_PAGE_SIZE)) : 1,
  );
  let tradeTotalPages = $derived(
    details ? Math.max(1, Math.ceil(details.trades.results.length / DIFF_PAGE_SIZE)) : 1,
  );
  let blockSlice = $derived(
    details
      ? details.blocks.results.slice(
          (blockPage - 1) * DIFF_PAGE_SIZE,
          blockPage * DIFF_PAGE_SIZE,
        )
      : [],
  );
  let tradeSlice = $derived(
    details
      ? details.trades.results.slice(
          (tradePage - 1) * DIFF_PAGE_SIZE,
          tradePage * DIFF_PAGE_SIZE,
        )
      : [],
  );

  // Alert state (Set Alert dropdown).
  let alertInterval = $state<ParityInterval | null>(null);
  let alertSubmitting = $state(false);
  // nextCheck ISO devuelto por el upstream cuando se setea un alert activo.
  // No viene en el GET — solo en la respuesta del POST. Lo guardamos para
  // mostrar "Next: HH:mm ET" debajo del boton Set Alert.
  let alertNextCheck = $state<string | null>(null);
  // Mini toast inline (sin libreria): { kind, message }. Auto-clear en 3s.
  let alertToast = $state<{ kind: 'success' | 'error'; message: string } | null>(null);
  let alertToastTimer: ReturnType<typeof setTimeout> | null = null;

  // AbortController por run para permitir cancel + invalidar resultados stale.
  let abortController: AbortController | null = null;
  let elapsedTimer: ReturnType<typeof setInterval> | null = null;

  // Guard contra re-disparo automatico del parity check. El $effect de Svelte
  // re-ejecuta cuando CUALQUIER dep tracked cambia, y como el modal vive
  // dentro de un parent que refresca su state cada minuto, el effect se
  // disparaba dos+ veces auto-recomputando.
  //
  // Solucion: solo correr start() cuando cambia el sessionId (o nunca corrio
  // todavia para este sessionId). El close del modal NO invalida el guard —
  // asi cuando el usuario va al Warmup modal y vuelve via "Back to Parity
  // Check", el result se preserva sin re-disparar el upstream. El operador
  // puede forzar re-run con el boton "Re-run parity" del footer.
  //
  // Invalidacion del guard:
  //   - cambio de sessionId (otra session abierta) → re-fetch automatico
  //   - boton "Re-run parity" → start() manual que dispara igual
  let lastStartedSessionId = $state<string | null>(null);
  $effect(() => {
    if (open && sessionId && sessionId !== lastStartedSessionId) {
      lastStartedSessionId = sessionId;
      void start();
    }
    // Importante: NO reseteamos lastStartedSessionId ni hacemos reset() al
    // cerrar, asi "Back to Parity Check" desde Warmup encuentra el result
    // tal cual lo dejamos.
  });

  async function start(): Promise<void> {
    if (!sessionId) return;
    cleanup();
    loading = true;
    error = null;
    result = null;
    details = null;
    expandedSection = null;
    elapsed = 0;
    elapsedTimer = setInterval(() => {
      elapsed += 1;
    }, 1000);
    abortController = new AbortController();
    try {
      const data = await runParityCheck(sessionId, abortController.signal);
      // Si abortamos en medio, ignoramos el resultado tardio.
      if (abortController.signal.aborted) return;
      result = data;
      loading = false;
      // Hidratar el estado del Set Alert en background (no bloquea UI).
      void hydrateAlert();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Cancelado por el usuario o por cierre del modal — silencio.
        return;
      }
      error = err instanceof Error ? err.message : 'Unknown error';
      loading = false;
    } finally {
      if (elapsedTimer) clearInterval(elapsedTimer);
      elapsedTimer = null;
    }
  }

  function cancel(): void {
    if (abortController) abortController.abort();
    loading = false;
    error = 'Cancelled by user';
  }

  function cleanup(): void {
    if (abortController) abortController.abort();
    abortController = null;
    if (elapsedTimer) {
      clearInterval(elapsedTimer);
      elapsedTimer = null;
    }
  }

  function reset(): void {
    cleanup();
    loading = false;
    elapsed = 0;
    error = null;
    result = null;
    details = null;
    detailsLoading = false;
    detailsError = null;
    expandedSection = null;
    alertInterval = null;
    alertNextCheck = null;
    alertSubmitting = false;
    alertToast = null;
    if (alertToastTimer) clearTimeout(alertToastTimer);
    alertToastTimer = null;
  }

  // ----- Set Alert dropdown -----
  async function hydrateAlert(): Promise<void> {
    if (!sessionId) return;
    try {
      const data = await fetchParityAlert(sessionId);
      alertInterval = data.interval; // ParityInterval | null
    } catch {
      // Silencio: si falla el GET, el dropdown muestra 'Set alert' default.
    }
  }

  async function submitAlert(interval: ParityInterval): Promise<void> {
    if (!sessionId || alertSubmitting) return;
    alertSubmitting = true;
    try {
      const res = await setParityAlert(sessionId, interval);
      alertInterval = interval;
      alertNextCheck = interval === 'off' ? null : (res.nextCheck ?? null);
      showToast(
        'success',
        interval === 'off' ? 'Alert turned off' : `Alert set to ${interval}`,
      );
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to set alert');
    } finally {
      alertSubmitting = false;
    }
  }

  function showToast(kind: 'success' | 'error', message: string): void {
    alertToast = { kind, message };
    if (alertToastTimer) clearTimeout(alertToastTimer);
    alertToastTimer = setTimeout(() => {
      alertToast = null;
    }, 3000);
  }

  function alertButtonLabel(): string {
    if (alertInterval && alertInterval !== 'off') return `Alert: ${alertInterval}`;
    return 'Set alert';
  }

  // Lazy load de details al primer expand de block o trade differences.
  async function loadDetailsIfNeeded(): Promise<void> {
    if (details || detailsLoading || !sessionId) return;
    detailsLoading = true;
    detailsError = null;
    try {
      details = await fetchParityDetails(sessionId);
    } catch (err) {
      detailsError = err instanceof Error ? err.message : 'Unknown error';
    } finally {
      detailsLoading = false;
    }
  }

  function toggleBlocks(): void {
    // Mutex: si ya esta abierto, lo cierra; sino abre y cierra trade.
    expandedSection = expandedSection === 'block' ? null : 'block';
    blockPage = 1;
    if (expandedSection === 'block') void loadDetailsIfNeeded();
  }

  function toggleTrades(): void {
    expandedSection = expandedSection === 'trade' ? null : 'trade';
    tradePage = 1;
    if (expandedSection === 'trade') void loadDetailsIfNeeded();
  }

  // ----- Formatters (safe: tolera null/undefined porque audit BUG #6) -----
  function fmtMoney(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }

  function fmtNum(n: number | null | undefined, digits = 0): string {
    if (n == null || !Number.isFinite(n)) return '—';
    return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }

  function fmtPct(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    return `${n.toFixed(1)}%`;
  }

  function fmtPF(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    return n.toFixed(2);
  }

  function fmtTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  // ----- Status visual -----
  function statusColor(s: ParityStatus | string | undefined): string {
    if (s === 'OK') return 'border-success/40 bg-success/15 text-success';
    if (s === 'DRIFT') return 'border-warning/40 bg-warning/15 text-warning';
    if (s === 'CRITICAL') return 'border-destructive/40 bg-destructive/15 text-destructive';
    return 'border-muted/40 bg-muted/15 text-muted-foreground';
  }

  // ----- Threshold helpers (BUG #1, #14 audit fix) -----
  // Hasta que el upstream devuelva tolerancias en cada response, usamos
  // DEFAULT_PARITY_TOLERANCES del shared (single source of truth para todo
  // el dashboard). Si el upstream las expone en el futuro, las leemos desde
  // `result.tolerances` y caemos al default.
  function tolFor(metric: keyof typeof DEFAULT_PARITY_TOLERANCES): number {
    return DEFAULT_PARITY_TOLERANCES[metric];
  }

  function isDiffWarn(metric: keyof typeof DEFAULT_PARITY_TOLERANCES, diff: number): boolean {
    return Math.abs(diff) > tolFor(metric);
  }

  // Color por signo para valores monetarios (P&L, slippage, diffs $). Igual
  // pattern que el resto del dashboard (SessionCard.totalPnL, SessionsTable,
  // EquityCurve): verde si > 0, rojo si < 0, neutral si 0.
  function signClass(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n) || n === 0) return '';
    return n > 0 ? 'text-success' : 'text-destructive';
  }

  // ----- Diff type label coloring (Block/Trade Differences rows) -----
  // Espejo del v1: cada tipo tiene un fondo + color distinto en la etiqueta
  // a la izquierda. MATCH va con fondo gris suave para no robar atencion;
  // MISMATCH amarillo (warning); MISSING_* rojo (destructive).
  function diffTypeStyle(type: string): { cls: string; label: string } {
    const upper = (type ?? '').toUpperCase();
    if (upper === 'MATCH') {
      return { cls: 'bg-muted text-muted-foreground', label: 'MATCH' };
    }
    if (upper === 'MISMATCH') {
      return { cls: 'bg-warning/20 text-warning', label: 'MISMATCH' };
    }
    if (upper === 'MISSING_IN_BT' || upper === 'MISSING-BT' || upper === 'MISSING BT') {
      return { cls: 'bg-destructive/20 text-destructive', label: 'MISSING BT' };
    }
    if (upper === 'MISSING_IN_LIVE' || upper === 'MISSING-LIVE' || upper === 'MISSING LIVE') {
      return { cls: 'bg-destructive/20 text-destructive', label: 'MISSING LIVE' };
    }
    return { cls: 'bg-muted text-muted-foreground', label: upper || '—' };
  }

  // Header del collapsible: si hay mismatch, mostramos badge "X differences"
  // destacado. Si match perfecto, badge verde "ALL MATCH".
  function matchBadgeStyle(allMatch: boolean): string {
    return allMatch
      ? 'border-success/40 bg-success/15 text-success'
      : 'border-warning/40 bg-warning/15 text-warning';
  }

  // Formatea direction (up/down) con flecha. Algunos diffs traen direction
  // dentro de live/bt; intentamos extraerla con prioridad live.
  function diffDirection(d: { live?: { direction?: string }; bt?: { direction?: string } }): string | null {
    const v = d.live?.direction ?? d.bt?.direction;
    return typeof v === 'string' ? v.toUpperCase() : null;
  }

  // Formatea time del diff (puede venir como string ISO o "YYYY-MM-DD HH:mm").
  function diffTime(d: { time?: string; live?: { time?: string }; bt?: { time?: string } }): string {
    const t = d.time ?? d.live?.time ?? d.bt?.time;
    return typeof t === 'string' ? t : '—';
  }
</script>

<Dialog bind:open onOpenChange={(o) => !o && onClose()}>
  <DialogContent class="max-h-[90vh] max-w-5xl overflow-y-auto">
    <!-- Header: mismo patron visual que SessionCard / Sessions tab.
         Symbol grande primary mono + sessionId copiable + status badge clickeable
         (abre Warmup) + Set Alert dropdown a la derecha. -->
    <div class="flex flex-wrap items-center justify-between gap-3 pr-8">
      <DialogTitle class="flex flex-wrap items-center gap-3">
        <span class="text-gold font-serif text-lg font-semibold tracking-tight">
          Parity Check
        </span>
        {#if result?.symbol}
          <span class="text-primary font-mono text-lg font-semibold">{result.symbol}</span>
        {/if}
        {#if sessionId}
          <CopyableSessionId sessionId={sessionId} size="md" stopPropagation={false} />
        {/if}
        {#if result}
          <button
            type="button"
            class={[
              'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors hover:brightness-110',
              statusColor(result.parity.status),
            ].join(' ')}
            onclick={() => sessionId && onOpenWarmup?.(sessionId)}
            title="Click to open Warmup Parity detail"
          >
            {#if result.parity.status === 'OK'}<CheckCircle2 class="size-3" />{:else if result.parity.status === 'DRIFT'}<AlertTriangle class="size-3" />{:else if result.parity.status === 'CRITICAL'}<XCircle class="size-3" />{/if}
            {result.parity.status}
          </button>
        {/if}
      </DialogTitle>

      <!-- Set Alert dropdown — solo visible cuando ya hay result.
           Si hay alert activa con nextCheck, lo mostramos debajo. -->
      {#if result}
        <div class="flex flex-col items-end gap-0.5">
          <DropdownMenu>
            <DropdownMenuTrigger>
              {#snippet child({ props })}
                <Button
                  {...props}
                  variant={alertInterval && alertInterval !== 'off' ? 'secondary' : 'outline'}
                  size="sm"
                  class="h-8 gap-1.5"
                  disabled={alertSubmitting}
                >
                  {#if alertSubmitting}
                    <Loader2 class="size-3.5 animate-spin" />
                  {:else if alertInterval && alertInterval !== 'off'}
                    <Bell class="size-3.5" />
                  {:else}
                    <BellOff class="size-3.5 opacity-70" />
                  {/if}
                  <span class="text-xs">{alertButtonLabel()}</span>
                  <ChevronDown class="text-muted-foreground size-3.5" />
                </Button>
              {/snippet}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-44">
              <DropdownMenuLabel>Re-check parity every</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {#each PARITY_INTERVAL_OPTIONS as opt (opt)}
                <DropdownMenuItem onclick={() => void submitAlert(opt)}>
                  <span class="flex-1">
                    {opt === 'off' ? 'Off' : opt}
                  </span>
                  {#if alertInterval === opt || (opt === 'off' && (!alertInterval || alertInterval === 'off'))}
                    <Check class="size-3.5" />
                  {/if}
                </DropdownMenuItem>
              {/each}
            </DropdownMenuContent>
          </DropdownMenu>
          {#if alertNextCheck && alertInterval && alertInterval !== 'off'}
            <span class="text-muted-foreground text-[10px] tabular-nums">
              Next: {fmtTime(alertNextCheck)} ET
            </span>
          {/if}
        </div>
      {/if}
    </div>

    <!-- Inline mini-toast (sin libreria) para feedback de Set Alert -->
    {#if alertToast}
      <div
        role="status"
        class={[
          'flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs',
          alertToast.kind === 'success'
            ? 'border-success/40 bg-success/10 text-success'
            : 'border-destructive/40 bg-destructive/10 text-destructive',
        ].join(' ')}
      >
        {#if alertToast.kind === 'success'}
          <CheckCircle2 class="size-3.5" />
        {:else}
          <AlertCircle class="size-3.5" />
        {/if}
        {alertToast.message}
      </div>
    {/if}
    {#if result}
      <DialogDescription class="text-muted-foreground text-xs">
        Checked at {fmtTime(result.timestamp)} ET · Click status badge for Warmup Parity detail.
      </DialogDescription>
    {:else}
      <DialogDescription class="sr-only">Parity check results modal</DialogDescription>
    {/if}

    <!-- Loading state -->
    {#if loading}
      <div class="flex flex-col items-center justify-center gap-3 py-12">
        <Loader2 class="text-primary size-8 animate-spin" />
        <div class="space-y-1 text-center">
          <p class="text-sm font-medium">Running parity check…</p>
          <p class="text-muted-foreground font-mono text-xs tabular-nums">{elapsed}s elapsed</p>
          {#if elapsed > 90}
            <p class="text-warning text-xs">
              This is taking longer than usual — the upstream may be saturated.
            </p>
          {:else if elapsed > 30}
            <p class="text-muted-foreground text-xs">Typically takes 1-60s depending on backlog.</p>
          {/if}
        </div>
        <Button variant="outline" size="sm" onclick={cancel}>
          <X class="size-3.5" />
          Cancel
        </Button>
      </div>
    {/if}

    <!-- Error state -->
    {#if error && !loading}
      <div
        role="alert"
        class="border-destructive/40 bg-destructive/10 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
      >
        <AlertCircle class="mt-0.5 size-4 shrink-0" />
        <div class="flex-1">
          <p class="font-medium">Parity check failed</p>
          <p class="text-xs opacity-80">{error}</p>
        </div>
        {#if sessionId}
          <Button variant="outline" size="sm" onclick={start}>Retry</Button>
        {/if}
      </div>
    {/if}

    <!-- Result -->
    {#if result && !loading}
      <!-- Summary table: 6 rows con Live / Backtest / Diff -->
      <div class="overflow-x-auto rounded-md border">
        <table class="w-full text-sm">
          <thead class="bg-muted/40 text-muted-foreground">
            <tr>
              <th class="px-3 py-2 text-left font-normal uppercase tracking-wider text-xs">Metric</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Live</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Backtest</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Diff</th>
            </tr>
          </thead>
          <tbody class="font-mono tabular-nums">
            <tr class="border-t">
              <td class="px-3 py-2 font-sans">Block Count</td>
              <td class="px-3 py-2 text-right">{fmtNum(result.live.blockCount)}</td>
              <td class="px-3 py-2 text-right">{fmtNum(result.bt.blockCount)}</td>
              <td class={['px-3 py-2 text-right', result.parity.blockCountDiff !== 0 && 'text-warning'].filter(Boolean).join(' ')}>
                {result.parity.blockCountDiff > 0 ? '+' : ''}{result.parity.blockCountDiff}
              </td>
            </tr>
            <tr class="border-t">
              <td class="px-3 py-2 font-sans">Trade Count</td>
              <td class="px-3 py-2 text-right">{fmtNum(result.live.tradeCount)}</td>
              <td class="px-3 py-2 text-right">{fmtNum(result.bt.tradeCount)}</td>
              <td class={['px-3 py-2 text-right', isDiffWarn('numTrades', result.parity.tradeCountDiff) && 'text-warning'].filter(Boolean).join(' ')}>
                {result.parity.tradeCountDiff > 0 ? '+' : ''}{result.parity.tradeCountDiff}
              </td>
            </tr>
            <tr class="border-t">
              <td class="px-3 py-2 font-sans">P&L</td>
              <td class={['px-3 py-2 text-right', signClass(result.live.pnl)].filter(Boolean).join(' ')}>
                {fmtMoney(result.live.pnl)}
              </td>
              <td class={['px-3 py-2 text-right', signClass(result.bt.pnl)].filter(Boolean).join(' ')}>
                {fmtMoney(result.bt.pnl)}
              </td>
              <td class={['px-3 py-2 text-right', signClass(result.parity.pnlDiff)].filter(Boolean).join(' ')}>
                {fmtMoney(result.parity.pnlDiff)}
              </td>
            </tr>
            <tr class="border-t">
              <td class="px-3 py-2 font-sans">Win Rate</td>
              <td class="px-3 py-2 text-right">{fmtPct(result.live.performance.winRate)}</td>
              <td class="px-3 py-2 text-right">{fmtPct(result.bt.performance.winRate)}</td>
              <td class={['px-3 py-2 text-right', isDiffWarn('winRate', (result.parity.performanceDiff.winRateDiff ?? 0) / 100) && 'text-warning'].filter(Boolean).join(' ')}>
                {fmtPct(result.parity.performanceDiff.winRateDiff)}
              </td>
            </tr>
            <tr class="border-t">
              <td class="px-3 py-2 font-sans">Profit Factor</td>
              <td class="px-3 py-2 text-right">{fmtPF(result.live.performance.profitFactor)}</td>
              <td class="px-3 py-2 text-right">{fmtPF(result.bt.performance.profitFactor)}</td>
              <td class={['px-3 py-2 text-right', isDiffWarn('profitFactor', result.parity.performanceDiff.profitFactorDiff ?? 0) && 'text-warning'].filter(Boolean).join(' ')}>
                {fmtPF(result.parity.performanceDiff.profitFactorDiff)}
              </td>
            </tr>
            <tr class="border-t">
              <td class="px-3 py-2 font-sans">Wins / Losses</td>
              <td class="px-3 py-2 text-right">
                <span class="text-success">{result.live.performance.wins ?? 0}</span> /
                <span class="text-destructive">{result.live.performance.losses ?? 0}</span>
              </td>
              <td class="px-3 py-2 text-right">
                <span class="text-success">{result.bt.performance.wins ?? 0}</span> /
                <span class="text-destructive">{result.bt.performance.losses ?? 0}</span>
              </td>
              <td class="px-3 py-2 text-right">
                {result.parity.performanceDiff.winsDiff ?? 0} / {result.parity.performanceDiff.lossesDiff ?? 0}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Polling-exit model gap banner (opcional). Aparece SOLO cuando el
           upstream marca `pollingExitModelGap`. El status sigue CRITICAL a
           proposito (decision de negocio): este banner es contexto ambar que
           EXPLICA por que el diff no es un bug, no un downgrade de severidad.
           Regla clave: `attributionReliable === false` => solo hechos duros,
           NUNCA un "monto causal de la brecha". Ver PollingExitModelGapStats. -->
      {#if result.parity.pollingExitModelGap}
        {@const gap = result.parity.pollingExitModelGapStats}
        <div class="border-warning/40 bg-warning/10 rounded-md border p-3.5">
          <div class="flex items-start gap-3">
            <AlertTriangle class="text-warning mt-0.5 size-4 shrink-0" />
            <div class="min-w-0 flex-1 space-y-2.5">
              <!-- Header editorial (gold/serif = sintesis curada, principio 7)
                   + mensaje corto del upstream. -->
              <div class="space-y-1">
                <p class="text-gold font-serif text-sm font-semibold tracking-tight">
                  Polling-exit model gap
                </p>
                {#if result.parity.displayMessage}
                  <p class="text-warning text-sm font-medium">
                    {result.parity.displayMessage}
                  </p>
                {/if}
              </div>

              <!-- Explicacion larga (por que hay brecha). Texto listo del upstream.
                   Color: warning atenuado, NO gris — el gris sobre tinte ambar
                   viola contraste + la regla "no gray on colored bg". Ancho: full
                   del banner (denso), no columna angosta con rio de espacio. -->
              {#if result.parity.pollingExitModelGapReason}
                <p class="text-warning/80 text-sm leading-relaxed">
                  {result.parity.pollingExitModelGapReason}
                </p>
              {/if}

              <!-- Hechos duros (sin monto causal). Solo si el upstream mando stats.
                   Labels en warning atenuado (no gris) por el mismo motivo. -->
              {#if gap}
                <div
                  class="border-warning/20 grid grid-cols-2 gap-x-4 gap-y-2.5 border-t pt-2.5 sm:grid-cols-4"
                >
                  <div>
                    <div class="text-warning/70 text-[10px] uppercase tracking-wider">
                      Live polling stops
                    </div>
                    <div class="font-mono text-sm font-semibold tabular-nums">
                      {fmtNum(gap.livePollingStopLossCount)}
                    </div>
                  </div>
                  <div>
                    <!-- Label deliberadamente NO causal: es el PnL propio de esos
                         stops, no "cuanto explica la brecha" (attributionReliable
                         false). Ver nota debajo. -->
                    <div class="text-warning/70 text-[10px] uppercase tracking-wider">
                      Live stops' own PnL
                    </div>
                    <div
                      class={['font-mono text-sm font-semibold tabular-nums', signClass(gap.livePollingStopLossPnl)].filter(Boolean).join(' ')}
                    >
                      {fmtMoney(gap.livePollingStopLossPnl)}
                    </div>
                  </div>
                  <div>
                    <div class="text-warning/70 text-[10px] uppercase tracking-wider">
                      Raw PnL diff
                    </div>
                    <div
                      class={['font-mono text-sm font-semibold tabular-nums', signClass(gap.rawPnlDiff)].filter(Boolean).join(' ')}
                    >
                      {fmtMoney(gap.rawPnlDiff)}
                    </div>
                  </div>
                  <div>
                    <div class="text-warning/70 text-[10px] uppercase tracking-wider">
                      Raw diff net of slip
                    </div>
                    <div
                      class={['font-mono text-sm font-semibold tabular-nums', signClass(gap.rawPnlDiffNetOfSlippage)].filter(Boolean).join(' ')}
                    >
                      {fmtMoney(gap.rawPnlDiffNetOfSlippage)}
                    </div>
                  </div>
                </div>

                <!-- Guardrail de atribucion: cuando el upstream no puede atribuir
                     un monto causal confiable, lo decimos explicitamente para que
                     el operador no lea "Live stops' own PnL" como el monto del gap. -->
                {#if gap.attributionReliable === false}
                  <div class="text-warning/70 flex items-start gap-1.5 text-[11px] leading-relaxed">
                    <Info class="mt-0.5 size-3 shrink-0" />
                    <span>
                      No reliable causal attribution: these are the live stops' own PnL, not the
                      portion of the diff they explain. Status stays CRITICAL by design.
                    </span>
                  </div>
                {/if}
              {/if}
            </div>
          </div>
        </div>
      {/if}

      <!-- Slippage card (feature nuevo del upstream, no estaba en audit) -->
      {#if result.slippage}
        <div class="bg-muted/20 grid grid-cols-2 gap-3 rounded-md border p-3 text-sm sm:grid-cols-3">
          <div>
            <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Slippage cost</div>
            <div class={['font-mono text-base font-semibold tabular-nums', signClass(result.slippage.totalSlippageCost)].filter(Boolean).join(' ')}>
              {fmtMoney(result.slippage.totalSlippageCost)}
            </div>
          </div>
          <div>
            <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Total slippage (pts)</div>
            <div class={['font-mono text-base font-semibold tabular-nums', signClass(result.slippage.totalSlippagePts)].filter(Boolean).join(' ')}>
              {result.slippage.totalSlippagePts > 0 ? '+' : ''}{result.slippage.totalSlippagePts?.toFixed(2)}
            </div>
          </div>
          <div>
            <div class="text-muted-foreground text-[10px] uppercase tracking-wider">PnL diff (net of slip)</div>
            <div class={['font-mono text-base font-semibold tabular-nums', signClass(result.slippage.pnlDiffNetOfSlippage)].filter(Boolean).join(' ')}>
              {fmtMoney(result.slippage.pnlDiffNetOfSlippage)}
            </div>
          </div>
          <div class="col-span-2 sm:col-span-3 text-muted-foreground text-xs">
            {result.slippage.tradesWithSlippage} of {result.live.tradeCount} trades had slippage.
            Entry: {result.slippage.totalEntrySlippagePts?.toFixed(2)} pts · Exit: {result.slippage.totalExitSlippagePts?.toFixed(2)} pts.
          </div>
        </div>
      {/if}

      <!-- Block Differences (collapsible, lazy) - rediseno estilo v1:
           header con badge MATCH/MISMATCH a fondo coloreado a la derecha;
           rows full-width con etiqueta tipo coloreada + timestamp + direction. -->
      <div class="overflow-hidden rounded-md border">
        <button
          type="button"
          class="hover:bg-muted/40 flex w-full items-center justify-between gap-2 px-4 py-3 transition-colors"
          onclick={toggleBlocks}
        >
          <span class="flex items-center gap-2">
            {#if blockExpanded}
              <ChevronDown class="size-4" />
            {:else}
              <ChevronRight class="size-4" />
            {/if}
            <span class="text-sm font-semibold">
              Block Differences
              {#if details}
                <span class="text-muted-foreground ml-1 font-normal">
                  ({details.blocks.differenceCount})
                </span>
              {/if}
            </span>
            <span
              class={[
                'ml-2 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                matchBadgeStyle(result.parity.blocksMatch),
              ].join(' ')}
            >
              {result.parity.blocksMatch ? 'ALL MATCH' : 'MISMATCH'}
            </span>
          </span>
          <span class="text-muted-foreground text-xs tabular-nums">
            Live {result.live.blockCount} · BT {result.bt.blockCount}
          </span>
        </button>
        {#if blockExpanded}
          <div class="border-t">
            {#if detailsLoading}
              <div class="text-muted-foreground flex items-center gap-2 px-4 py-3 text-sm">
                <Loader2 class="size-4 animate-spin" /> Loading details…
              </div>
            {:else if detailsError}
              <p class="text-destructive px-4 py-3 text-sm">{detailsError}</p>
            {:else if details}
              <div class="text-muted-foreground border-b px-4 py-2 text-xs">
                {details.blocks.matchCount} matches · {details.blocks.differenceCount} differences{#if details.blocks.realDifferenceCount != null && details.blocks.realDifferenceCount !== details.blocks.differenceCount}
                  &nbsp;({details.blocks.realDifferenceCount} real)
                {/if}
              </div>
              {#if details.blocks.results.length === 0}
                <p class="text-muted-foreground px-4 py-3 text-sm">No differences to show.</p>
              {:else}
                <ul class="divide-y">
                  {#each blockSlice as d, i (i + (blockPage - 1) * DIFF_PAGE_SIZE)}
                    {@const ts = diffTypeStyle(String(d.type))}
                    {@const dir = diffDirection(d)}
                    <li class="hover:bg-muted/30 flex items-center gap-3 px-4 py-1.5 transition-colors">
                      <span
                        class={[
                          'inline-flex w-[120px] shrink-0 items-center justify-center rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                          ts.cls,
                        ].join(' ')}
                      >
                        {ts.label}
                      </span>
                      <span class="font-mono text-xs tabular-nums">{diffTime(d)}</span>
                      {#if dir}
                        <span
                          class={[
                            'font-mono text-xs font-semibold uppercase',
                            dir === 'UP' ? 'text-success' : 'text-destructive',
                          ].join(' ')}
                        >
                          {dir}
                        </span>
                      {/if}
                    </li>
                  {/each}
                </ul>
                {#if blockTotalPages > 1}
                  {@const from = (blockPage - 1) * DIFF_PAGE_SIZE + 1}
                  {@const to = Math.min(blockPage * DIFF_PAGE_SIZE, details.blocks.results.length)}
                  <div
                    class="text-muted-foreground flex items-center justify-between gap-2 border-t px-4 py-2 text-xs"
                  >
                    <span class="tabular-nums">
                      {from}–{to} of {details.blocks.results.length}
                    </span>
                    <div class="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        class="h-7"
                        disabled={blockPage <= 1}
                        onclick={() => (blockPage = Math.max(1, blockPage - 1))}
                      >
                        <ChevronLeft class="size-3.5" />
                      </Button>
                      <span class="px-2 tabular-nums">
                        {blockPage} / {blockTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        class="h-7"
                        disabled={blockPage >= blockTotalPages}
                        onclick={() => (blockPage = Math.min(blockTotalPages, blockPage + 1))}
                      >
                        <ChevronRight class="size-3.5" />
                      </Button>
                    </div>
                  </div>
                {/if}
              {/if}
            {/if}
          </div>
        {/if}
      </div>

      <!-- Trade Differences (mismo patron que Block Differences) -->
      <div class="overflow-hidden rounded-md border">
        <button
          type="button"
          class="hover:bg-muted/40 flex w-full items-center justify-between gap-2 px-4 py-3 transition-colors"
          onclick={toggleTrades}
        >
          <span class="flex items-center gap-2">
            {#if tradeExpanded}
              <ChevronDown class="size-4" />
            {:else}
              <ChevronRight class="size-4" />
            {/if}
            <span class="text-sm font-semibold">
              Trade Differences
              {#if details}
                <span class="text-muted-foreground ml-1 font-normal">
                  ({details.trades.differenceCount})
                </span>
              {/if}
            </span>
            <span
              class={[
                'ml-2 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                matchBadgeStyle(result.parity.tradesMatch),
              ].join(' ')}
            >
              {result.parity.tradesMatch ? 'ALL MATCH' : 'MISMATCH'}
            </span>
          </span>
          <span class="text-muted-foreground text-xs tabular-nums">
            Live {result.live.tradeCount} · BT {result.bt.tradeCount}
          </span>
        </button>
        {#if tradeExpanded}
          <div class="border-t">
            {#if detailsLoading}
              <div class="text-muted-foreground flex items-center gap-2 px-4 py-3 text-sm">
                <Loader2 class="size-4 animate-spin" /> Loading details…
              </div>
            {:else if detailsError}
              <p class="text-destructive px-4 py-3 text-sm">{detailsError}</p>
            {:else if details}
              <div class="text-muted-foreground border-b px-4 py-2 text-xs">
                {details.trades.matchCount} matches · {details.trades.differenceCount} differences{#if details.trades.pairedCount != null}
                  &nbsp;({details.trades.pairedCount} paired, {details.trades.liveOnlyCount} live-only, {details.trades.btOnlyCount} bt-only)
                {/if}
              </div>
              {#if details.trades.results.length === 0}
                <p class="text-muted-foreground px-4 py-3 text-sm">No differences to show.</p>
              {:else}
                <ul class="divide-y">
                  {#each tradeSlice as d, i (i + (tradePage - 1) * DIFF_PAGE_SIZE)}
                    {@const ts = diffTypeStyle(String(d.type))}
                    {@const liveCloseReason =
                      typeof d.live?.closeReason === 'string' ? d.live.closeReason : null}
                    {@const isPollingExit =
                      liveCloseReason != null && liveCloseReason.startsWith('polling-')}
                    <li class="hover:bg-muted/30 flex items-center gap-3 px-4 py-1.5 transition-colors">
                      <span
                        class={[
                          'inline-flex w-[120px] shrink-0 items-center justify-center rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                          ts.cls,
                        ].join(' ')}
                      >
                        {ts.label}
                      </span>
                      {#if d.tradeIndex != null}
                        <span class="text-muted-foreground font-mono text-xs tabular-nums">
                          #{d.tradeIndex}
                        </span>
                      {/if}
                      <!-- Side y entry/exit prices si disponibles -->
                      {#if d.live?.side || d.bt?.side}
                        {@const side = (d.live?.side ?? d.bt?.side ?? '').toUpperCase()}
                        <span
                          class={[
                            'font-mono text-xs font-semibold uppercase',
                            side === 'LONG' ? 'text-success' : 'text-destructive',
                          ].join(' ')}
                        >
                          {side}
                        </span>
                      {/if}
                      <span class="text-muted-foreground font-mono text-xs">
                        {d.live?.entryTime ?? d.bt?.entryTime ?? ''}
                      </span>
                      {#if isPollingExit}
                        <!-- PX5 chip: este trade salió intra-candle por polling 5s.
                             El backtest evalúa al cierre del minuto, así que estos
                             trades son los principales candidatos a diverger. -->
                        <span
                          class="inline-flex items-center gap-1 rounded border border-cyan-500/60 bg-cyan-500/15 px-1.5 py-0.5 font-mono text-[10px] font-bold tabular-nums text-yellow-400"
                          title={`Polling exit (5s intra-candle): ${liveCloseReason}`}
                        >
                          <Zap
                            class="h-2.5 w-2.5 fill-yellow-400 text-yellow-400"
                            strokeWidth={2.5}
                          />
                          PX5
                        </span>
                      {/if}
                    </li>
                  {/each}
                </ul>
                {#if tradeTotalPages > 1}
                  {@const from = (tradePage - 1) * DIFF_PAGE_SIZE + 1}
                  {@const to = Math.min(tradePage * DIFF_PAGE_SIZE, details.trades.results.length)}
                  <div
                    class="text-muted-foreground flex items-center justify-between gap-2 border-t px-4 py-2 text-xs"
                  >
                    <span class="tabular-nums">
                      {from}–{to} of {details.trades.results.length}
                    </span>
                    <div class="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        class="h-7"
                        disabled={tradePage <= 1}
                        onclick={() => (tradePage = Math.max(1, tradePage - 1))}
                      >
                        <ChevronLeft class="size-3.5" />
                      </Button>
                      <span class="px-2 tabular-nums">
                        {tradePage} / {tradeTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        class="h-7"
                        disabled={tradePage >= tradeTotalPages}
                        onclick={() => (tradePage = Math.min(tradeTotalPages, tradePage + 1))}
                      >
                        <ChevronRight class="size-3.5" />
                      </Button>
                    </div>
                  </div>
                {/if}
              {/if}
            {/if}
          </div>
        {/if}
      </div>

    {/if}

    <div class="flex justify-end gap-2 pt-2">
      {#if (result || error) && !loading}
        <Button variant="outline" size="sm" onclick={start} disabled={!sessionId}>
          <RotateCw class="size-3.5" />
          Re-run parity
        </Button>
      {/if}
      <Button variant="outline" size="sm" onclick={onClose}>Close</Button>
    </div>
  </DialogContent>
</Dialog>
